// Per-opponent interpolation clocks (online play). The shared renderers normally
// draw every worm at one global render time (server time minus a fixed delay). That
// is fine on paper, but a fixed delay is a fixed tax: a player with a clean stream
// pays a 100ms-stale view they don't need, while a bursty stream needs more cushion
// than any fixed number. The delay only has to cover JITTER - transit latency is
// already absorbed by the server-clock anchoring - so the right cushion is a
// measured, per-opponent quantity.
//
// This gives each opponent its OWN smooth render clock at a MEASURED delay:
//   age(track)   = estimated server now - the track's newest real data time
//                  (the opponent's upstream lag + broadcast batching + network jitter)
//   peak(track)  = peak-hold of age with slow decay (rides the p~100 of recent spikes,
//                  forgets old ones over ~10s)
//   cushion      = clamp(peak + PAD, floor, CAP)
//   clock        = server now - cushion, rate-limited to real-time speed, monotonic,
//                  never past the data itself.
// A clean stream renders ~(its real jitter + PAD) behind the server; a spiky or
// laggy opponent automatically gets the bigger cushion they need, without slowing
// anyone else. Freshness is measured from each track's newest segment as
// startTime + duration (the real geometric end): the server stamps a
// client-controlled worm's last segment endTime forward each tick, but never
// touches duration, so startTime + duration is the trustworthy newest-data time.

// TUNING PHILOSOPHY: delay is worse than stutter. Delay corrupts player
// interaction (you cut off a worm that was actually already past you) constantly
// and invisibly; a stutter is a brief cosmetic blip on an opponent, often in the
// periphery. So the estimator is deliberately forgiving toward spikes: it reacts
// to them (the cushion still jumps so the SAME spike repeating doesn't starve the
// clock) but forgets them fast, keeping the delay pinned near the true floor.
const PAD = 0.02;         // s on top of the measured peak (frame quantization headroom)
const CAP = 0.25;         // s: absolute cushion ceiling (reachable only via the floor param)
const PEAK_DECAY = 0.10;  // s of peak forgotten per second (a spike's influence fades ~1.5s)
const ABS_FLOOR = 0.02;   // s: hard minimum cushion (~one render frame of headroom vs measurement noise)
// While the clock is behind its target it runs this much faster than real time to
// close the gap smoothly (a starvation hold leaves a deficit, and a decaying
// cushion pulls the target ahead faster than real time). Must outpace PEAK_DECAY
// or recovery lags the shrinking cushion; without it deficits persist until the
// 0.4s desync snap, which is a visible teleport.
const CATCHUP = 0.15;     // fraction of real-time speed added while behind target
// The peak (and thus what the cushion can learn from one incident) is bounded:
// a long stall costs at most a ~170ms cushion for ~1.5s, then it's forgotten. A
// connection that is PERSISTENTLY worse keeps re-feeding the peak and holds its
// bigger cushion for as long as the badness actually lasts.
const PEAK_MAX = 0.15;

interface TrackEst {
  r: number;          // the smoothed render clock
  peak: number;       // peak-hold age (s)
  cushion: number;    // current target cushion (s)
  age: number;        // last measured age (s)
  holds: number;      // frames the clock could not advance (starved by data)
  maxAge: number;     // worst age seen since the last data arrival (the jitter sample)
  lastLatest: number; // newest data time at the previous tick (arrival detection)
}

export interface InterpTrackStats {
  trackId: string;
  delayMs: number;   // how far this track renders behind estimated server-now
  cushionMs: number; // the measured target cushion
  ageMs: number;     // current data age
  peakMs: number;    // decaying peak age
  holds: number;     // starved frames since round start
}

export function createOpponentInterpolator() {
  // Keyed per TRACK (playerId_wormId), not per player: the twin power-up gives one
  // player two worms/tracks, and a per-player clock let them clobber each other's
  // value (the surviving twin then rendered at the wrong interpolated time).
  const est = new Map<string, TrackEst>();
  // Learned jitter peaks carried across resets: a new round rewinds the CLOCKS, not
  // the network, so each track re-seeds from the cushion it already earned instead
  // of re-learning it through visible stutters at every round start. Track ids
  // recur across rounds (playerId_wormId), and the peak keeps decaying normally
  // once ticking resumes, so a stale carry corrects itself within seconds.
  const carry = new Map<string, number>();
  let lastNow = 0;

  function reset() {
    est.forEach((e, id) => carry.set(id, e.peak));
    est.clear();
    lastNow = 0;
  }

  // Advance every opponent's clock one frame. `serverNow` is the estimated current
  // server time (no render delay); `globalRenderTime` remains the fallback clock for
  // tracks not ticked yet; `floorSec` is the minimum cushion. Call once per frame,
  // before rendering.
  //
  // The clock is rate-limited to real-time speed (+dt) and capped at `desired`
  // rather than advanced-then-corrected: an unconditional-advance scheme leaves a
  // compounding overshoot that collapses the cushion (see git history). Monotonic
  // within a round so a trail never rewinds.
  function tick(gameState: any, serverNow: number, nowMs: number, floorSec: number) {
    const dt = lastNow === 0 ? 0 : Math.max(0, Math.min(0.25, (nowMs - lastNow) / 1000));
    lastNow = nowMs;
    const floor = Math.max(ABS_FLOOR, floorSec || 0);
    const tracks = gameState.wormPathSegments || {};
    for (const id in tracks) {
      const segs = tracks[id];
      if (!segs || !segs.length) continue;
      const last = segs[segs.length - 1];
      const realLatest = last.startTime + (last.duration || 0); // newest real data
      const age = Math.max(0, serverNow - realLatest);
      let e = est.get(id);
      if (!e) {
        const learned = Math.min(carry.get(id) || 0, PEAK_MAX); // last round's jitter peak, if any
        const peak0 = Math.min(Math.max(age, learned), PEAK_MAX);
        e = { r: 0, peak: peak0, cushion: Math.min(CAP, Math.max(floor, peak0 + PAD)), age, holds: 0, maxAge: 0, lastLatest: realLatest };
        e.r = Math.min(serverNow - e.cushion, realLatest);
        est.set(id, e);
        continue;
      }
      e.age = age;
      // Learn jitter ONLY from a live stream: record the worst age reached between
      // arrivals and feed it into the peak-hold when the next data actually lands.
      // A track that stopped (round over, a dead worm, a frozen opponent) delivers
      // nothing, so its ever-growing age teaches nothing - previously the inter-round
      // pause registered as a fake multi-second "spike" that pegged the cushion at
      // the cap for minutes. The peak decays continuously and is bounded (PEAK_MAX)
      // so even a real stall costs seconds of elevated cushion, not minutes.
      e.maxAge = Math.max(e.maxAge, age);
      e.peak = Math.max(0, e.peak - PEAK_DECAY * dt);
      if (realLatest > e.lastLatest + 1e-9) {
        e.peak = Math.max(e.peak, Math.min(e.maxAge, PEAK_MAX));
        e.maxAge = 0;
        e.lastLatest = realLatest;
      }
      e.cushion = Math.min(CAP, Math.max(floor, e.peak + PAD));
      // A finished track (worm died) never grows again: drop the cushion so the
      // clock runs through to the death point instead of freezing short of it.
      const dead = last.type === "worm_died";
      const desired = dead ? realLatest : Math.min(serverNow - e.cushion, realLatest);
      let r;
      if (Math.abs(desired - e.r) > 0.4) {
        r = desired; // big desync (new round / first data): snap
      } else {
        r = Math.min(desired, e.r + dt * (1 + CATCHUP)); // ~real-time speed, never ahead of desired
        if (r < e.r) r = e.r; // monotonic within a round (never rewind the trail)
        if (r === e.r && dt > 0 && !dead) e.holds++; // starved: data (or cushion) didn't allow progress
      }
      e.r = r;
    }
  }

  // The render time to use for one worm's TRACK; falls back to the global clock for
  // any track we have not ticked yet (e.g. the very first frame).
  function get(trackId: string, fallback: number): number {
    const e = est.get(trackId);
    return e === undefined ? fallback : e.r;
  }

  // Live stats for the in-game netstats overlay (per track, vs the given server-now).
  function getStats(serverNow: number): InterpTrackStats[] {
    const out: InterpTrackStats[] = [];
    est.forEach((e, trackId) => {
      out.push({
        trackId,
        delayMs: Math.round((serverNow - e.r) * 1000),
        cushionMs: Math.round(e.cushion * 1000),
        ageMs: Math.round(e.age * 1000),
        peakMs: Math.round(e.peak * 1000),
        holds: e.holds,
      });
    });
    return out;
  }

  return { tick, get, reset, getStats };
}
